import { StatesMachine } from "components/pipeline-components/classes/state-machine/state-machine";
import { StateProps } from "components/pipeline-components/classes/state-machine/state";
import { BLOCKS_BY_OPERATION } from "components/pipeline-components/node-catalog";
import { FiZap } from "react-icons/fi";
import { IconType } from "react-icons";
import { BlockItem } from "./types";
import { ICON_OPTIONS } from "./constants";
import { StateFilterValue } from "components/pipeline-components/classes/state-machine/filters";
import { State } from "components/pipeline-components/classes/state-machine/state";

export function buildSequenciaBloco(blocks: BlockItem[]): string {
  const machine = new StatesMachine({ states: [] });

  for (const block of blocks) {
    machine.addOperationState(block.operation);
    applyPreConfigToMachine(machine, block);
  }

  const start = machine.states.find((s) => s.operation === "START");
  const end = machine.states.find((s) => s.operation === "END");
  const orderedVisible = blocks
    .map((b) =>
      machine.getVisibleStates().find((s) => s.operation === b.operation)
    )
    .filter((s): s is NonNullable<typeof s> => !!s);

  if (start && end) {
    if (orderedVisible.length > 0) {
      machine.connectStates(start.id, orderedVisible[0].id);
      for (let i = 0; i < orderedVisible.length - 1; i++) {
        machine.connectStates(orderedVisible[i].id, orderedVisible[i + 1].id);
      }
      machine.connectStates(
        orderedVisible[orderedVisible.length - 1].id,
        end.id
      );
    } else {
      machine.connectStates(start.id, end.id);
    }
  }

  return JSON.stringify(machine.formatToSend());
}

export function parseBlocksFromSequencia(sequenciaBloco?: string): BlockItem[] {
  if (!sequenciaBloco) return [];
  try {
    const parsed = JSON.parse(sequenciaBloco);
    const machine = new StatesMachine(parsed);
    const visible = machine
      .getVisibleStates()
      .filter((s) => !["START", "END"].includes(s.operation));
    return visible.map((s) => ({
      operation: s.operation,
      preConfig: buildPreConfigByOperation(machine, s.operation),
      expanded: false
    }));
  } catch {
    return [];
  }
}

export function buildPreConfigFromState(state: {
  [key: string]: any;
}): Partial<StateProps> {
  const config: Partial<StateProps> = {};
  const preConfigFields: (keyof StateProps)[] = [
    "banco",
    "convenio",
    "codigoTabela",
    "idConvenio",
    "tipoCartao",
    "tabela",
    "usuarioDigitacao",
    "usuarioDigitacaoSenha",
    "cpfUsuarioCertificado",
    "tipoColunaMargem",
    "operacao",
    "nome",
    "mensagem",
    "quantidadeMaximaTelLead",
    "mappedFields",
    "name",
    "qtdEstimadaEnvioDia",
    "enabledMensagemSaudacao",
    "type",
    "usedHeaders",
    "mensagemNormal",
    "mensagemSaudacao",
    "wppSessionIds",
    "fileType",
    "filename",
    "fileSize"
  ];
  for (const field of preConfigFields) {
    if (state[field as keyof typeof state] !== undefined) {
      (config as any)[field] = state[field as keyof typeof state];
    }
  }
  return config;
}

export function applyPreConfigToMachine(
  machine: StatesMachine,
  block: BlockItem
) {
  const preConfig = block.preConfig || {};
  if (Object.keys(preConfig).length === 0) return;

  if (block.operation === "REFIN") {
    const executorStates = machine.states
      .filter(
        (state) => state.operation === "REFIN" && state.stateType === "EXECUTOR"
      )
      .sort((a, b) => a.id - b.id);
    const bancos = splitCsv(preConfig.banco as string | undefined);
    bancos.forEach((banco, index) => {
      const existingState = executorStates[index];

      if (existingState) {
        existingState.banco = banco;
        existingState.convenio = preConfig.convenio;
        existingState.codigoTabela = preConfig.codigoTabela;
        return;
      }

      machine.addState(
        new State({
          id: StatesMachine.getNextStateIdFrom(machine.states),
          operation: "REFIN",
          stateType: "EXECUTOR",
          banco,
          convenio: preConfig.convenio,
          codigoTabela: preConfig.codigoTabela
        })
      );
    });

    executorStates
      .slice(bancos.length)
      .forEach((state) => machine.removeState(state.id));
    return;
  }

  if (block.operation === "SAQUE_COMPLEMENTAR") {
    const executorStates = machine.states
      .filter(
        (state) =>
          state.operation === "SAQUE_COMPLEMENTAR" &&
          state.stateType === "EXECUTOR"
      )
      .sort((a, b) => a.id - b.id);

    if (!preConfig.banco) {
      executorStates.forEach((state) => machine.removeState(state.id));
      return;
    }

    const existingState = executorStates[0];
    if (existingState) {
      existingState.banco = preConfig.banco;
      existingState.codigoTabela = preConfig.codigoTabela;
      existingState.idConvenio = preConfig.idConvenio;
      existingState.tipoCartao = preConfig.tipoCartao;
    } else {
      machine.addState(
        new State({
          id: StatesMachine.getNextStateIdFrom(machine.states),
          operation: "SAQUE_COMPLEMENTAR",
          stateType: "EXECUTOR",
          banco: preConfig.banco,
          idConvenio: preConfig.idConvenio,
          tipoCartao: preConfig.tipoCartao,
          codigoTabela: preConfig.codigoTabela
        })
      );
    }

    executorStates.slice(1).forEach((state) => machine.removeState(state.id));
    return;
  }

  if (block.operation === "FILTRO") {
    const configJson = preConfig.configJson as
      | Record<string, StateFilterValue>
      | undefined;
    if (!configJson || Object.keys(configJson).length === 0) return;
    machine.updateOperationConfig("FILTRO", (currentConfig) => {
      Object.assign(currentConfig, configJson);
    });
    return;
  }

  machine.updateOperationState(block.operation, (state) => {
    Object.assign(state, preConfig);
  });
}

export function buildPreConfigByOperation(
  machine: StatesMachine,
  operation: StateProps["operation"]
): Partial<StateProps> {
  if (operation === "REFIN") {
    const refinExecutors = machine.states.filter(
      (s) => s.operation === "REFIN" && s.stateType === "EXECUTOR"
    );

    if (!refinExecutors.length) return {};
    return {
      banco: refinExecutors
        .map((s) => s.banco)
        .filter(Boolean)
        .join(", "),
      convenio: refinExecutors[0]?.convenio,
      codigoTabela: refinExecutors[0]?.codigoTabela
    };
  }

  if (operation === "SAQUE_COMPLEMENTAR") {
    const saqueExecutor = machine.states.find(
      (s) => s.operation === "SAQUE_COMPLEMENTAR" && s.stateType === "EXECUTOR"
    );
    return saqueExecutor ? buildPreConfigFromState(saqueExecutor) : {};
  }

  if (operation === "FILTRO") {
    const filtroState = machine.getFilterState();
    const configJson = filtroState?.configJson?.formatToSend();
    return configJson ? { configJson } : {};
  }

  const state = machine.getExecutorState(operation);
  return state ? buildPreConfigFromState(state) : {};
}

function splitCsv(value?: string): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function blockChainLabel(blocks: BlockItem[]): string {
  return blocks
    .map((b) => BLOCKS_BY_OPERATION[b.operation]?.label ?? b.operation)
    .join(" → ");
}

export function formatDate(iso?: string): string {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR");
}

export function getIconComponent(key?: string): IconType {
  return ICON_OPTIONS.find((o) => o.key === key)?.icon ?? FiZap;
}
